import { useEffect, useMemo } from 'react';
import { motion } from 'motion/react';
import styled from 'styled-components';
import { Link } from '~/utils/navigation';
import Icon, { ICON_BELL_LINE } from '~/components/icon/index.js';
import IconButtonClose from '../misc/icon-button-close.js';
import breakpoint from '../../utils/style-utils';
import useUIContext from '~/ui/use-ui-context';
import useTranslations from '../translation/use-translations';

const variants = {
	enter: {
		y: 0,
		opacity: 1,
		transition: {
			type: 'spring',
			damping: 16,
			duration: 0.2
		}
	},
	exit: {
		y: '-100%',
		opacity: 0,
		transition: {
			type: 'spring',
			damping: 16,
			duration: 0.2
		}
	}
};

export const NotificationsWrapper = styled.div`
	top: ${props => (props.hasStaticHeader ? 'var(--regulationHeight)' : '0')};
	${props =>
		props.withinSidebar &&
		breakpoint.lg`
				top: auto;
				width: var(--detailWidth);
			`}
`;

const NotificationWrapper = styled.div`
	margin: ${props => props.customMargin};
	border-bottom: 1px solid rgba(var(--base-01), 0.3);
`;

const AnimatedLink = motion(Link);

const NotificationWithEffect = motion(NotificationWrapper);

const ICON_MAP = {
	bell: <Icon type={ICON_BELL_LINE} />
};

const Notifications = ({
	id: notificationId,
	actions,
	shouldOpen,
	message,
	messageKey,
	color = '',
	buttonLink = '',
	linkCB,
	buttonText = '',
	buttonTextKey,
	buttonInline,
	buttonCB,
	buttonLinkInternal,
	title = '',
	titleKey,
	icon,
	autoHide,
	autoHideDuration,
	openInSidebar,
	centerAligned,
	onCloseClickCallback,
	onBluebarClick,
	customMargin,
	buttonBottom
}) => {
	const translationsArray = useMemo(
		() => [messageKey, titleKey, buttonTextKey].filter(v => v),

		[messageKey, titleKey, buttonTextKey]
	);
	const { translations, isLoadingTranslations } = useTranslations(translationsArray);
	const { withinSidebar = false, hasStaticHeader = false } = useUIContext();

	useEffect(() => {
		if (!autoHide) return;

		const timer = setTimeout(() => {
			actions.hideNotification(notificationId);
		}, autoHideDuration || 20000);

		return () => {
			clearTimeout(timer);
		};
	}, [autoHide, autoHideDuration, actions, notificationId]);

	if ((translationsArray.length > 0 && isLoadingTranslations) || !shouldOpen) {
		return false;
	}

	return (
		((withinSidebar && openInSidebar) || (!withinSidebar && !openInSidebar)) && (
			<NotificationWithEffect
				variants={variants}
				animate={shouldOpen ? 'enter' : 'exit'}
				initial="exit"
				exit="exit"
				hasStaticHeader={hasStaticHeader}
				withinSidebar={withinSidebar}
				customMargin={customMargin}
				className={`no-transition notification-wrap notification-wrap--open ${color} ${
					buttonBottom ? 'button-bottom' : ''
				}`}
				onClick={e => {
					e.stopPropagation();
					if (onBluebarClick) {
						onBluebarClick();
						actions.hideNotification(notificationId);
					}
				}}
			>
				<IconButtonClose
					onClick={e => {
						e.stopPropagation();
						if (onCloseClickCallback) {
							onCloseClickCallback();
						}
						actions.hideNotification(notificationId);
					}}
					className={`at-icon-close page-head__close u-absolute ${
						ICON_MAP[icon]
							? 'u-pos-tr1'
							: buttonInline && buttonCB
								? 'u-absolute--center-vertically u-ml1'
								: 'u-pos-tl10'
					} ${(buttonText || buttonTextKey) && !centerAligned ? 'icon-alignment-fix' : ''}`}
				/>
				{ICON_MAP[icon] && <span className="u-absolute u-pos-tl10 header-icon">{ICON_MAP[icon]}</span>}
				<div
					className={`notification-wrap__error ${centerAligned ? 'u-fxb-c' : ''} u-flex u-align-vert ${
						buttonInline && buttonCB ? 'u-flex-1' : ''
					}
							`}
					data-at="notification-bar-header"
				>
					<span>
						{title && (
							<h4 className="notification-wrap__error-text-header">
								{(titleKey && translations[titleKey]) || title}
							</h4>
						)}

						<span
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: (messageKey && translations[messageKey]) || message
							}}
							className="notification-wrap__error-text-msg"
						/>
					</span>
					{buttonInline && buttonCB && (
						<button
							className="btn btn--ghost btn--auto u-ml1"
							onClick={e => {
								e.stopPropagation();
								buttonCB(e);
							}}
						>
							{(buttonTextKey && translations[buttonTextKey]) || buttonText}
						</button>
					)}
				</div>
				{buttonLink && (
					<div className="u-p1 notification-button">
						<a className="btn btn--ghost" href={buttonLink} onClick={linkCB}>
							{(buttonTextKey && translations[buttonTextKey]) || buttonText}
						</a>
					</div>
				)}
				{buttonLinkInternal && (
					<div className="u-p1 notification-button">
						<AnimatedLink whileTap={{ scale: 0.9 }} className="btn btn--ghost" href={buttonLinkInternal}>
							{(buttonTextKey && translations[buttonTextKey]) || buttonText}
						</AnimatedLink>
					</div>
				)}
				{!buttonInline && buttonCB && (
					<div className="u-p1 notification-button">
						<button
							className="btn btn--ghost"
							onClick={e => {
								e.stopPropagation();
								buttonCB();
								actions.hideNotification(notificationId);
							}}
						>
							{(buttonTextKey && translations[buttonTextKey]) || buttonText}
						</button>
					</div>
				)}
			</NotificationWithEffect>
		)
	);
};

export default Notifications;
